/**
 * Trait Mutation Engine
 *
 * Applies a TraitEvent's raw deltas to a student's traitProfile using:
 *
 * 1. TIME MODIFIER  — fast answers get a boost, slow ones a penalty
 *    timeTaken / timeLimit < 0.35  → ×1.30 (fast, confident)
 *    timeTaken / timeLimit 0.35–0.65 → ×1.00 (normal)
 *    timeTaken / timeLimit 0.65–0.85 → ×0.80 (deliberate)
 *    timeTaken / timeLimit > 0.85  → ×0.60 (struggled)
 *    No time data → ×1.00
 *
 * 2. SCORE MODIFIER — higher performance multiplies deltas
 *    score ≥ 80  → ×1.30
 *    score 60–79 → ×1.00
 *    score 40–59 → ×0.75
 *    score < 40  → ×0.50
 *    No score data → ×1.00
 *
 * 3. DIFFICULTY MODIFIER — hard tasks earn bigger boosts
 *    difficulty 3 (hard)   → ×1.20
 *    difficulty 2 (medium) → ×1.00
 *    difficulty 1 (easy)   → ×0.85
 *
 * 4. CORRECT/INCORRECT MODIFIER (quiz only)
 *    correct   → ×1.0  (full delta)
 *    incorrect → ×0.25 (small negative signal — never punish hard)
 *
 * 5. DIMINISHING RETURNS — prevents runaway trait scores
 *    traitScore ≥ 88 → modifier ×0.20
 *    traitScore ≥ 75 → modifier ×0.45
 *    traitScore ≥ 60 → modifier ×0.75
 *    traitScore < 60 → modifier ×1.00
 *
 * 6. SOURCE WEIGHT — psychometric events carry higher weight than single quizzes
 *    psychometric  → ×3.0  (baseline)
 *    course        → ×1.5  (module completion)
 *    activity_log  → ×1.2  (self-reported, trusted but weighted less)
 *    exam_result   → ×2.0  (external result, high credibility)
 *    quiz          → ×1.0
 *    game          → ×0.8  (games are fun, moderate signal)
 *
 * Final delta is clamped so no single event shifts any trait more than ±12 points.
 * Result trait score is clamped to [0, 100].
 */

import { TraitEvent, TraitEventSource } from '@/types/traitEvents';
import { TraitProfile } from './traitScoring';

// ─── Modifier tables ──────────────────────────────────────────────────────────

const SOURCE_WEIGHT: Record<TraitEventSource, number> = {
  psychometric:  3.0,
  exam_result:   2.0,
  course:        1.5,
  activity_log:  1.2,
  quiz:          1.0,
  game:          0.8,
};

function timeModifier(timeTaken?: number, timeLimit?: number): number {
  if (!timeTaken || !timeLimit || timeLimit === 0) return 1.0;
  const ratio = timeTaken / timeLimit;
  if (ratio < 0.35) return 1.30;
  if (ratio < 0.65) return 1.00;
  if (ratio < 0.85) return 0.80;
  return 0.60;
}

function scoreModifier(score?: number): number {
  if (score === undefined || score === null) return 1.0;
  if (score >= 80) return 1.30;
  if (score >= 60) return 1.00;
  if (score >= 40) return 0.75;
  return 0.50;
}

function difficultyModifier(difficulty?: 1 | 2 | 3): number {
  if (!difficulty) return 1.0;
  return difficulty === 3 ? 1.20 : difficulty === 2 ? 1.00 : 0.85;
}

function correctnessModifier(correct?: boolean): number {
  if (correct === undefined) return 1.0;
  return correct ? 1.0 : 0.25;
}

function diminishingReturns(currentScore: number): number {
  if (currentScore >= 88) return 0.20;
  if (currentScore >= 75) return 0.45;
  if (currentScore >= 60) return 0.75;
  return 1.00;
}

// ─── Core functions ───────────────────────────────────────────────────────────

/**
 * Compute the effective delta for a single trait given event context.
 * rawDelta is the authored value from the question/game/activity definition.
 */
export function computeEffectiveDelta(
  rawDelta: number,
  currentScore: number,
  event: Pick<TraitEvent, 'source' | 'meta'>
): number {
  const { meta, source } = event;

  const composite =
    rawDelta *
    SOURCE_WEIGHT[source] *
    timeModifier(meta.timeTakenSeconds, meta.timeLimitSeconds) *
    scoreModifier(meta.score) *
    difficultyModifier(meta.difficulty) *
    correctnessModifier(meta.correct) *
    diminishingReturns(currentScore);

  // Clamp to ±12 per event per trait
  const clamped = Math.max(-12, Math.min(12, composite));
  return clamped;
}

/**
 * Apply a single TraitEvent to a traitProfile.
 * Returns a new traitProfile (does not mutate the original).
 */
export function applyTraitEvent(
  profile: TraitProfile,
  event: TraitEvent
): TraitProfile {
  const updated = { ...profile };

  for (const [traitId, rawDelta] of Object.entries(event.traitDeltas)) {
    const currentScore = updated[traitId] ?? 0;
    const effectiveDelta = computeEffectiveDelta(rawDelta, currentScore, event);
    updated[traitId] = Math.round(
      Math.max(0, Math.min(100, currentScore + effectiveDelta))
    );
  }

  return updated;
}

/**
 * Replay all trait events in chronological order to produce the current profile.
 * Used when recalculating from scratch (e.g. after event correction or reweighting).
 */
export function replayTraitEvents(
  baselineProfile: TraitProfile,
  events: TraitEvent[]
): TraitProfile {
  const sorted = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  return sorted.reduce(applyTraitEvent, baselineProfile);
}

// ─── Delta generators for common sources ─────────────────────────────────────

/**
 * Generate trait deltas for a game result.
 * Uses normalised score (0–1) × primaryTrait base delta.
 */
export function gameResultToDeltas(
  primaryTraits: string[],
  score: number,
  maxScore: number
): Record<string, number> {
  const normalised = maxScore > 0 ? score / maxScore : 0;
  const baseDelta = 2 + normalised * 4; // 2–6 range
  return Object.fromEntries(primaryTraits.map((t) => [t, baseDelta]));
}

/**
 * Generate trait deltas for an activity log entry.
 * Uses the core traits of the activity type + outcome multiplier.
 */
export function activityLogToDeltas(
  coreTraits: string[],
  outcomeMultiplier: number,
  actualScore?: number
): Record<string, number> {
  const scoreMod = actualScore !== undefined ? scoreModifier(actualScore) : 1.0;
  const base = 2 * outcomeMultiplier * scoreMod;
  return Object.fromEntries(coreTraits.map((t) => [t, parseFloat(base.toFixed(2))]));
}
